import apiClient from '@/services/apiClient';

let onSaleFetchPromise = null;

async function loadOnSaleProducts({ commit }) {
    const cached = localStorage.getItem('onSaleProducts');
    const timestamp = localStorage.getItem('onSaleProductsTimestamp');
    const cacheAge = Date.now() - parseInt(timestamp || '0', 10);

    if (cached && cacheAge < 120000) {
        const cachedData = JSON.parse(cached);
        commit('SET_ON_SALE_PRODUCTS', cachedData);
        return { results: cachedData };
    }

    if (onSaleFetchPromise) {
        return onSaleFetchPromise;
    }

    onSaleFetchPromise = (async () => {
        try {
            const response = await apiClient.get('/products/on-sale-fast/', {
                timeout: 8000
            });

            if (!response.data || !response.data.results) {
                commit('SET_ON_SALE_PRODUCTS', []);
                return { results: [] };
            }

            const products = response.data.results;
            localStorage.setItem('onSaleProducts', JSON.stringify(products));
            localStorage.setItem('onSaleProductsTimestamp', Date.now().toString());
            commit('SET_ON_SALE_PRODUCTS', products);
            return response.data;
        } catch (error) {
            const staleCache = localStorage.getItem('onSaleProducts');
            if (staleCache) {
                const cachedData = JSON.parse(staleCache);
                commit('SET_ON_SALE_PRODUCTS', cachedData);
                return { results: cachedData };
            }
            commit('SET_ON_SALE_PRODUCTS', []);
            throw error;
        } finally {
            onSaleFetchPromise = null;
        }
    })();

    return onSaleFetchPromise;
}

// Products module for Vuex 4
const products = {
    namespaced: true,
    state: {
        products: [],
        onSaleProducts: [],
        loading: false,
        loadingOnSale: false,
        error: null,
        currentProduct: null
    },mutations: {
        SET_PRODUCTS(state, products) {
            state.products = products;
        },
        SET_ON_SALE_PRODUCTS(state, products) {
            state.onSaleProducts = products;
        },
        SET_LOADING(state, isLoading) {
            state.loading = isLoading;
        },
        SET_LOADING_ON_SALE(state, isLoading) {
            state.loadingOnSale = isLoading;
        },
        SET_ERROR(state, error) {
            state.error = error;
        },
        SET_CURRENT_PRODUCT(state, product) {
            state.currentProduct = product;
        },
        ADD_PRODUCT(state, product) {
            state.products.push(product);
        },
        UPDATE_PRODUCT(state, updatedProduct) {
            const index = state.products.findIndex(p => p.id === updatedProduct.id);
            if (index !== -1) {
                state.products.splice(index, 1, updatedProduct);
            }
        },
        DELETE_PRODUCT(state, productId) {
            state.products = state.products.filter(p => p.id !== productId);
        }
    },    actions: {        async fetchProducts({ commit }) {
            commit('SET_LOADING', true);
            commit('SET_ERROR', null);
            try {
                console.log('Fetching latest products...');
                
                // Primero intentar sin autenticación (productos públicos)
                const response = await apiClient.get('/products/', {
                    params: { page_size: 24 }
                });
                
                console.log('Latest products response:', response.data);
                commit('SET_PRODUCTS', response.data);
            } catch (error) {
                console.error('Error fetching products:', error);
                
                // En caso de error, intentar con autenticación
                try {
                    const token = localStorage.getItem('accessToken');
                    if (token) {
                        const authResponse = await apiClient.get('/products/', {
                            headers: { Authorization: `Bearer ${token}` },
                            params: { page_size: 24 }
                        });
                        console.log('Products with auth:', authResponse.data);
                        commit('SET_PRODUCTS', authResponse.data);
                        return;
                    }
                } catch (authError) {
                    console.error('Auth attempt also failed:', authError);
                }
                
                commit('SET_ERROR', error.response?.data || 'Error fetching products');
            } finally {
                commit('SET_LOADING', false);
            }
        },async fetchProductsOnSale({ commit, state }) {
            commit('SET_LOADING_ON_SALE', true);
            commit('SET_ERROR', null);
            try {
                return await loadOnSaleProducts({ commit, state });
            } catch (error) {
                console.error('Error fetching products on sale:', error);
                commit('SET_ERROR', error.response?.data || 'Error fetching products on sale');
                return { results: [] };
            } finally {
                commit('SET_LOADING_ON_SALE', false);
            }
        },

        async preloadProductsOnSale({ commit, state }) {
            if (state.onSaleProducts.length > 0) {
                const timestamp = localStorage.getItem('onSaleProductsTimestamp');
                const cacheAge = Date.now() - parseInt(timestamp || '0', 10);
                if (cacheAge < 60000) {
                    return;
                }
            }

            if (onSaleFetchPromise) {
                return;
            }

            try {
                await loadOnSaleProducts({ commit, state });
            } catch (error) {
                console.log('Preload failed silently:', error.message);
            }
        },async createProduct({ commit }, productData) {
            commit('SET_LOADING', true);
            commit('SET_ERROR', null);
            try {
                const token = localStorage.getItem('accessToken');
                const response = await apiClient.post('/products/', productData, {
                    headers: { 
                        Authorization: `Bearer ${token}`,
                        'Content-Type': 'application/json'
                    }
                });
                commit('ADD_PRODUCT', response.data);
                return response.data;
            } catch (error) {
                commit('SET_ERROR', error.response?.data || 'Error creating product');
                throw error;
            } finally {
                commit('SET_LOADING', false);
            }
        },
        async fetchProductById({ commit }, productId) {
            commit('SET_LOADING', true);
            commit('SET_ERROR', null);
            try {
                const token = localStorage.getItem('accessToken');
                const response = await apiClient.get(`/products/${productId}/`, {
                    headers: { Authorization: `Bearer ${token}` }
                });
                commit('SET_CURRENT_PRODUCT', response.data);
                return response.data;
            } catch (error) {
                commit('SET_ERROR', error.response?.data || 'Error fetching product');
                throw error;
            } finally {
                commit('SET_LOADING', false);
            }
        },
        async updateProduct({ commit }, { productId, productData }) {
            commit('SET_LOADING', true);
            commit('SET_ERROR', null);
            try {
                const token = localStorage.getItem('accessToken');
                const response = await apiClient.put(`/products/${productId}/`, productData, {
                    headers: { 
                        Authorization: `Bearer ${token}`,
                        'Content-Type': 'application/json'
                    }
                });
                commit('UPDATE_PRODUCT', response.data);
                return response.data;
            } catch (error) {
                commit('SET_ERROR', error.response?.data || 'Error updating product');
                throw error;
            } finally {
                commit('SET_LOADING', false);
            }
        },
        async deleteProduct({ commit }, productId) {
            commit('SET_LOADING', true);
            commit('SET_ERROR', null);
            try {
                const token = localStorage.getItem('accessToken');
                await apiClient.delete(`/products/${productId}/`, {
                    headers: { Authorization: `Bearer ${token}` }
                });
                commit('DELETE_PRODUCT', productId);
            } catch (error) {
                commit('SET_ERROR', error.response?.data || 'Error deleting product');
                throw error;
            } finally {
                commit('SET_LOADING', false);
            }
        },
        
        // Limpiar cache de productos en oferta
        clearOnSaleCache({ commit }) {
            localStorage.removeItem('onSaleProducts');
            localStorage.removeItem('onSaleProductsTimestamp');
            commit('SET_ON_SALE_PRODUCTS', []);
            console.log('On sale products cache cleared');
        }
    },
    getters: {        // Getter optimizado para productos en oferta
        onSaleProducts: (state) => {
            return state.onSaleProducts || [];
        },
        // Getter para obtener productos regulares (no en oferta)
        regularProducts: (state) => {
            if (!Array.isArray(state.products?.results)) {
                return [];
            }
            return state.products.results.slice(0, 12);
        }}
};

export default products;